import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

// Redux
import { connect } from 'react-redux';
import { setAlert } from '../../../../redux/actions/alertActions';

// Server
import { getAlert } from '../../../../api';

// Components
import ActivityFeed from '../../../../components/ActivityFeed';
import { ListNote } from '../../../../components/ListControls';

// Constants
import { T, FONT_MONO } from '../../../../constants/tokens';
import { statusColor, toneColor, incidentDuration, formatWhen, formatTimelineTime } from '../format';

/** An ongoing alert changes; a finished one does not. */
const REFRESH_MS = 10000;

const useStyles = makeStyles({
  back: {
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    height: 36,
    padding: '0 14px',
    marginBottom: 24,
    borderRadius: 6,
    border: '1px solid ' + T.hairline,
    background: T.elevated,
    color: T.ink,
    cursor: 'pointer',
    '&:hover': { background: T.surfaceSoft },
    '&:focus-visible': { outline: '2px solid ' + T.focus, outlineOffset: 2 }
  }
});

const eyebrow = { fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: T.body };
const card = { background: T.elevated, border: '1px solid ' + T.hairline, borderRadius: 8, padding: 24 };
const sectionTitle = { margin: 0, fontSize: 20, fontWeight: 600, lineHeight: '28px', letterSpacing: '-0.4px', color: T.ink };

/**
 * One alert: what it was, where it came from, how long it ran, what it sent
 * out, and everything recorded while it was in force.
 */
function AlertDetail({ windowWidth, setError }) {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const [incident, setIncident] = useState(null);
  const [missing, setMissing] = useState(false);
  const mounted = useRef(true);

  const load = useCallback(() => {
    getAlert(id)
      .then((result) => {
        if (mounted.current) setIncident(result);
      })
      .catch((error) => {
        if (!mounted.current) return;
        if (String(error).includes('No such alert')) setMissing(true);
        else setError(error ? error : 'Could not retrieve the alert.');
      });
  }, [id, setError]);

  useEffect(() => {
    mounted.current = true;
    setIncident(null);
    setMissing(false);
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  // Keep an ongoing alert's timeline current.
  const ongoing = incident?.getIsActive() === true;
  useEffect(() => {
    if (!ongoing) return undefined;
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [ongoing, load]);

  const factColumns = windowWidth >= 1180 ? 5 : windowWidth >= 700 ? 3 : 1;

  // In its own block: the view pills above are inline, and would otherwise
  // pull the button up beside them.
  const back = (
    <div>
      <button type='button' className={classes.back} onClick={() => history.push('/dashboard/alerts')}>
        ← Alert history
      </button>
    </div>
  );

  if (missing) return <>{back}<ListNote>This alert does not exist, or is not an alert.</ListNote></>;
  if (incident == null) return <>{back}<ListNote>Loading…</ListNote></>;

  const color = statusColor(incident.getStatus());
  const started = incident.getStartedAt();
  const facts = [
    { k: 'Started', v: formatWhen(started) },
    { k: 'Ended', v: incident.getIsActive() ? 'Still in force' : formatWhen(incident.getEndedAt()) },
    { k: 'Duration', v: incidentDuration(incident) },
    { k: 'Scope', v: incident.getIsSchoolWide() ? 'Whole school' : (incident.getRoom() ?? 'One room') },
    { k: 'Mode', v: incident.getIsDrill() ? 'Drill' : 'Real event' }
  ];
  const dispatch = incident.getDispatch();

  return (
    <>
      {back}

      {/* What it was */}
      <section style={{ ...card, marginBottom: 24, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: 32, borderBottom: '1px solid ' + T.hairlineSoft }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ width: 10, height: 10, borderRadius: 9999, background: color, flex: 'none' }} />
            <span style={eyebrow}>
              {[incident.getStatus(), incident.getIsDrill() ? 'drill' : 'real event', started ? started.toLocal().toFormat('LLL d, yyyy') : null].filter(Boolean).join(' · ')}
            </span>
            <div style={{ flex: 1 }} />
            <span style={{ ...eyebrow, color: T.muted }}>#{incident.getID()}</span>
          </div>
          <h1 style={{ margin: 0, fontSize: 32, fontWeight: 600, lineHeight: '40px', letterSpacing: '-1.28px', color: T.ink }}>
            {incident.getTitle()}
          </h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
            <span style={eyebrow}>Raised from</span>
            <span style={{ fontSize: 14, lineHeight: '20px', color: T.body }}>
              {[incident.getSource(), incident.getRaisedBy() ? 'by ' + incident.getRaisedBy() : null].filter(Boolean).join(' · ')}
            </span>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(' + factColumns + ', minmax(0, 1fr))' }}>
          {facts.map((f, i) => (
            <div
              key={f.k}
              style={{
                padding: '20px 24px',
                borderLeft: i % factColumns === 0 ? 0 : '1px solid ' + T.hairlineSoft,
                borderTop: i >= factColumns ? '1px solid ' + T.hairlineSoft : 0
              }}
            >
              <div style={eyebrow}>{f.k}</div>
              <div style={{ fontFamily: FONT_MONO, fontSize: 16, fontWeight: 500, lineHeight: '24px', color: T.ink, marginTop: 4 }}>{f.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What it sent out. Emergencies only: nothing else texts or calls. */}
      {dispatch &&
        <section style={{ ...card, marginBottom: 24 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
            <h2 style={sectionTitle}>Notification &amp; dispatch</h2>
            <span style={{ fontSize: 14, color: T.body }}>{dispatch.note}</span>
          </div>
          {dispatch.rows.map((row, i) => (
            <div
              key={row.channel}
              style={{
                display: 'grid',
                gridTemplateColumns: '8px minmax(0, 1fr) auto auto',
                alignItems: 'center',
                gap: 16,
                padding: '14px 0',
                borderTop: i === 0 ? 0 : '1px solid ' + T.hairlineSoft
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: toneColor(row.tone) }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: T.ink }}>{row.channel}</div>
                <div style={{ fontSize: 14, lineHeight: '20px', color: T.body }}>{row.detail}</div>
              </div>
              <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.muted, whiteSpace: 'nowrap' }}>
                {row.at == null ? '' : '+' + row.at + 's'}
              </span>
              <span style={{ fontSize: 14, fontWeight: 500, color: T.ink, whiteSpace: 'nowrap' }}>{row.state}</span>
            </div>
          ))}
        </section>}

      {/* Everything recorded while it was in force */}
      <section style={card}>
        <h2 style={{ ...sectionTitle, marginBottom: 8 }}>Timeline</h2>
        <ActivityFeed
          events={incident.getTimeline()}
          formatTime={(t) => formatTimelineTime(t, started)}
          emptyText='Nothing else was recorded while this alert was in force.'
        />
      </section>
    </>
  );
}

AlertDetail.propTypes = {
  windowWidth: PropTypes.number,
  setError: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  windowWidth: state.ui.windowSize.width
});

const mapDispatchToProps = (dispatch) => ({
  setError: (msg) => setAlert(dispatch, msg)
});

export default connect(mapStateToProps, mapDispatchToProps)(AlertDetail);
